import { UnreachableCaseError } from '@bounty/utils'
import { acceptValidationCodeToReason } from '../../utils/acceptValidationReasonText'
import * as B from '../../BountyDetailParts'
import { useVideoPlayerState } from '../../../../provider/videoPlayer'
const AcceptableBountyCard = ({
  nextPage = 'rules',
}: {
  nextPage?: 'select-brief' | 'content-type' | 'survey' | 'rules'
}) => {
  const bounty = B.useBounty()
  const [, setVideoPlayerState] = useVideoPlayerState()

  return (
    <B.StatusCard>
      <B.StatusHeading>Get started!</B.StatusHeading>
      <B.StatusParagraph>Your bounty is ready to accept!</B.StatusParagraph>
      <B.StatusButtonLink
        size="$sm"
        event="Bounty Status Card Accept Bounty Clicked"
        href={`/bounties/${bounty.id}/accept/${nextPage}`}
        onPress={() => {
          setVideoPlayerState((x) => ({
            ...x,
            videoUrl: null,
            poster: null,
            videoConfig: {},
          }))
        }}
      >
        {bounty.type === 'SHOPIFY_STORE_ORDER_GIFT'
          ? 'Accept Gift'
          : 'Accept Bounty'}
      </B.StatusButtonLink>
    </B.StatusCard>
  )
}

const NotAcceptableBountyCard = () => {
  const bounty = B.useBounty()
  return (
    <B.StatusCard>
      <B.StatusHeading>Pending</B.StatusHeading>
      <B.StatusParagraph>
        {acceptValidationCodeToReason(
          bounty.availableToAcceptBounty?.validationCode,
        )}
      </B.StatusParagraph>
    </B.StatusCard>
  )
}

const PendingBountyCard = ({ isRetail = false }: { isRetail?: boolean }) => {
  const bounty = B.useBounty()

  const nextPage = bounty.briefId
    ? bounty.contentTypesAllowed?.length === 1 &&
      bounty.contentTypesAllowed?.includes('TIKTOK_VIDEO')
      ? 'rules'
      : 'content-type'
    : 'select-brief'

  return bounty.availableToAcceptBounty?.isValid ? (
    <AcceptableBountyCard nextPage={isRetail ? 'survey' : nextPage} />
  ) : (
    <NotAcceptableBountyCard />
  )
}

export const BountyDetailPending = ({
  data,
  ...props
}: Omit<B.PageProps, 'children'>) => {
  switch (data.bounty.type) {
    case 'SHOPIFY_AD_HOC':
      return (
        <B.Page data={data} {...props}>
          <B.SparkCodes />
          <B.SectionGroup>
            <B.TitleRow />
            {data.bounty.isExpired ? <B.ExpiredCard /> : <PendingBountyCard />}
            <B.HowBountyWorksCard />
          </B.SectionGroup>
          <B.MinimumPayout />
          {data.bounty.briefId ? <B.BriefsCard /> : <B.BriefList />}
          <B.OrderDetails />
          <B.CommunityRules />
        </B.Page>
      )
    case 'RETAIL':
      return (
        <B.Page data={data} {...props}>
          <B.SparkCodes />
          <B.SectionGroup>
            <B.TitleRow />
            {data.bounty.isExpired ? (
              <B.ExpiredCard />
            ) : (
              <PendingBountyCard isRetail={true} />
            )}
            <B.RetailDetailsCard />
            <B.HowBountyWorksCard />
          </B.SectionGroup>
          <B.MinimumPayout />
          {data.bounty.briefId ? <B.BriefsCard /> : <B.BriefList />}
          <B.CommunityRules />
        </B.Page>
      )
    case 'SHOPIFY_STORE_ORDER':
    case 'SHOPIFY_STORE_ORDER_GIFT':
      return (
        <B.Page data={data} {...props}>
          <B.SparkCodes />
          <B.SectionGroup>
            <B.TitleRow />
            {data.bounty.isExpired ? <B.ExpiredCard /> : <PendingBountyCard />}
            <B.HowBountyWorksCard />
          </B.SectionGroup>
          <B.MinimumPayout />
          {data.bounty.briefId ? <B.BriefsCard /> : <B.BriefList />}
          <B.OrderDetails />
          <B.CommunityRules />
        </B.Page>
      )

    default:
      throw new UnreachableCaseError(data.bounty.type)
  }
}
